import { useState, useEffect } from 'react';
import { Star, Sparkles, Users, Award, Clock, Heart, Eye, Target, Lightbulb, Home, Building, Leaf, CheckCircle, ArrowRight, Play, Calendar, Zap, Phone, Layers } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { useLocation, Link } from 'react-router-dom';
const AboutPage = () => {
  const { pathname } = useLocation();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting
          }));
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const stats = [
    { icon: Users, value: "500+", label: "Clients Satisfaits", color: "from-blue-500 to-cyan-500" },
    { icon: Award, value: "200+", label: "Projets Réalisés", color: "from-amber-500 to-orange-500" },
    { icon: Clock, value: "+13", label: "Années d'Expérience", color: "from-purple-500 to-pink-500" },
    { icon: Heart, value: "97%", label: "Satisfaction Client", color: "from-red-500 to-rose-500" }
  ];

  const values = [
    {
      icon: Eye,
      title: "Vision",
      description: "Transformer chaque espace en une œuvre d'art fonctionnelle qui reflète la personnalité unique de nos clients.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Target,
      title: "Mission",
      description: "Offrir des solutions de décoration innovantes et durables qui dépassent les attentes de nos clients.",
      color: "from-amber-500 to-orange-500"
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description: "Intégrer les dernières tendances et technologies pour créer des espaces modernes et intemporels.",
      color: "from-purple-500 to-pink-500"
    }
  ];

  const services = [
    {
      icon: Home,
      title: "Décoration Intérieure",
      description: "Création d'espaces intérieurs harmonieux et fonctionnels qui reflètent votre style de vie.",
      features: ["Design personnalisé", "Optimisation d'espace", "Choix des matériaux", "Suivi de projet"]
    },
    {
      icon: Building,
      title: "Design Commercial",
      description: "Conception d'espaces commerciaux attractifs qui renforcent votre image de marque.",
      features: ["Identité visuelle", "Ergonomie", "Éclairage professionnel", "Mobilier sur mesure"]
    },
    {
      icon: Leaf,
      title: "Aménagement Extérieur",
      description: "Transformation de vos espaces extérieurs en véritables havres de paix.",
      features: ["Paysagisme", "Terrasses", "Éclairage extérieur", "Mobilier outdoor"]
    }
  ];

  const CLOUDINARY_BASE = "https://res.cloudinary.com/dpk0ydobt/image/upload/q_auto,f_auto/placof/";

  const manager = {
    name: "Amir HADJ ALI",
    role: "Fondateur & Gérant",
    image: `${CLOUDINARY_BASE}logos/gérant.jpg`,
    speciality: "Expert en décoration et aménagement d'intérieur",
    description: "Avec plus de 15 ans d'expérience dans le domaine, Amir a fondé Placof Décor avec une vision claire : offrir des solutions de décoration innovantes et accessibles à tous. Grâce à sa maîtrise du placoplâtre, il a su transformer chaque espace en une œuvre harmonieuse, alliant modernité, fonctionnalité et esthétisme."
  };

  return (
    <>
      <Helmet>
        <title>À Propos - Placof Décor | Expert en Décoration Intérieure</title>
        <meta
          name="description"
          content="Découvrez l'histoire de Placof Décor, notre expertise en décoration intérieure et notre engagement pour des designs uniques à Sfax et en Tunisie."
        />
        <meta name="keywords" content="entreprise décoration Sfax, histoire Placof Décor, expert faux plafond Tunisie" />

        {/* Open Graph */}
        <meta property="og:title" content="À Propos - Placof Décor | Expert en Décoration Intérieure" />

        <link rel="canonical" href={`https://placofdecor.tn${pathname}`} />

      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50 overflow-hidden">
        {/* Particules de fond */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full opacity-20 animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${i * 0.3}s`,
                transform: `translate(${mousePosition.x * 0.01}px, ${mousePosition.y * 0.005}px)`
              }}
            />
          ))}
        </div>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto text-center relative z-10">
            <div
              className="space-y-8"
              style={{
                transform: `translateY(${scrollY * 0.1}px)`
              }}
            >
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-orange-100 px-6 py-3 rounded-full border border-amber-200/50 shadow-lg">
                <Sparkles className="w-5 h-5 text-amber-600 animate-pulse" />
                <span className="text-amber-700 font-semibold">À Propos de Nous</span>
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent leading-tight">
                Créateurs d'Espaces
                <span className="block text-4xl sm:text-5xl lg:text-6xl mt-2">
                  Exceptionnels
                </span>
              </h1>

              <p className="text-xl sm:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                Depuis 2012, nous transformons vos rêves en réalité à travers des designs
                <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent font-semibold"> innovants</span> et
                <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent font-semibold"> personnalisés</span>.
              </p>

              {/* Boutons CTA */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button className="group relative flex items-center gap-3 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white px-8 py-4 rounded-2xl font-bold shadow-2xl hover:shadow-amber-500/25 transition-all duration-500 overflow-hidden transform hover:scale-105">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  <Play className="w-5 h-5 relative z-10 group-hover:animate-bounce" />
                  <Link to="/projects"> <span className="relative z-10">Découvrir nos Projets</span> </Link>
                </button>

                <button className="group flex items-center gap-3 bg-white/80 backdrop-blur-sm text-gray-700 px-8 py-4 rounded-2xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-500 border border-amber-100/50 hover:border-amber-200">
                  <Calendar className="w-5 h-5 group-hover:animate-bounce" />
                  <Link to="/contact"> <span>Prendre Rendez-vous</span> </Link>
                  <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>

          {/* Élément décoratif */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-full blur-3xl -z-10" />
        </section>

        {/* Statistiques */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative">
          <div className="container mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  id={`stat-${index}`}
                  data-animate
                  className={`group relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-amber-100/50 text-center transform hover:scale-105 ${isVisible[`stat-${index}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                  style={{
                    transitionDelay: `${index * 0.1}s`
                  }}
                >
                  <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-r ${stat.color} flex items-center justify-center group-hover:rotate-12 transition-transform duration-300`}>
                    <stat.icon className="w-8 h-8 text-white group-hover:animate-bounce" />
                  </div>

                  <div className="space-y-2">
                    <div className={`text-4xl font-black bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                      {stat.value}
                    </div>
                    <p className="text-gray-600 font-semibold">{stat.label}</p>
                  </div>

                  {/* Effet de brillance */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-3xl" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Notre Histoire */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-amber-50/50 to-orange-50/50">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent mb-6">
                Notre Histoire
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Une passion pour la décoration qui a évolué en expertise reconnue
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <div className="prose prose-lg text-gray-600">
                  <p className="text-xl leading-relaxed mb-6">
                    Tout a commencé en 2012 avec une vision simple : <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent font-semibold">transformer les espaces ordinaires en lieux extraordinaires</span>.
                  </p>

                  <p className="text-lg leading-relaxed mb-6">
                    Fondée par un gérant passionné, Placof Décor s'est rapidement imposée comme une référence en Tunisie dans la décoration intérieure et extérieure, en particulier grâce à son expertise dans l'utilisation du placoplâtre pour des réalisations modernes, élégantes et sur mesure.

                  </p>

                  <p className="text-lg leading-relaxed">
                    Aujourd'hui, nous sommes fiers d'avoir accompagné plus de 500 clients dans la réalisation de leurs projets, allant de la simple rénovation à la création d'espaces commerciaux complexes.
                  </p>
                </div>

                <div className="flex flex-wrap gap-4">
                  {["Créativité", "Excellence", "Innovation", "Passion"].map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-orange-100 px-4 py-2 rounded-full border border-amber-200/50 text-amber-700 font-semibold"
                    >
                      <Star className="w-4 h-4" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <div className="h-48 bg-gradient-to-br from-amber-200 to-orange-300 rounded-2xl shadow-xl" />
                    <div className="h-32 bg-gradient-to-br from-orange-200 to-red-300 rounded-2xl shadow-xl" />
                  </div>
                  <div className="space-y-4 mt-8">
                    <div className="h-32 bg-gradient-to-br from-purple-200 to-pink-300 rounded-2xl shadow-xl" />
                    <div className="h-48 bg-gradient-to-br from-blue-200 to-cyan-300 rounded-2xl shadow-xl" />
                  </div>
                </div>

                {/* Élément décoratif flottant */}
                <div className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl shadow-2xl flex items-center justify-center transform rotate-12">
                  <Sparkles className="w-8 h-8 text-white animate-pulse" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Nos Valeurs */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent mb-6">
                Nos Valeurs
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Les principes qui guident chacune de nos créations
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <div
                  key={index}
                  id={`value-${index}`}
                  data-animate
                  className={`group relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-amber-100/50 text-center transform hover:scale-105 ${isVisible[`value-${index}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                  style={{
                    transitionDelay: `${index * 0.2}s`
                  }}
                >
                  <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-r ${value.color} flex items-center justify-center group-hover:rotate-12 transition-transform duration-300`}>
                    <value.icon className="w-10 h-10 text-white group-hover:animate-bounce" />
                  </div>

                  <h3 className={`text-2xl font-bold mb-4 bg-gradient-to-r ${value.color} bg-clip-text text-transparent`}>
                    {value.title}
                  </h3>

                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>

                  {/* Effet de brillance */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-3xl" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-amber-50/50 to-orange-50/50">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent mb-6">
                Nos Expertises
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Des services complets pour tous vos projets de décoration
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  id={`service-${index}`}
                  data-animate
                  className={`group relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-amber-100/50 transform hover:scale-105 ${isVisible[`service-${index}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                  style={{
                    transitionDelay: `${index * 0.2}s`
                  }}
                >
                  <div className="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center group-hover:rotate-12 transition-transform duration-300">
                    <service.icon className="w-8 h-8 text-white group-hover:animate-bounce" />
                  </div>

                  <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                    {service.title}
                  </h3>

                  <p className="text-gray-600 leading-relaxed mb-6">
                    {service.description}
                  </p>

                  <ul className="space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-3 text-gray-600">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Effet de brillance */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-3xl" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section Gérant Unique */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent mb-6">
                Notre Gérant
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                L'expertise et la passion au service de vos projets
              </p>
            </div>

            <div className="max-w-2xl mx-auto">
              <div className="group relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-amber-100/50 text-center transform hover:scale-[1.02]">
                <div className="relative mb-6">
                  <div className="w-48 h-48 mx-auto rounded-3xl overflow-hidden bg-gradient-to-br from-amber-200 to-orange-300 shadow-2xl group-hover:rotate-1 transition-transform duration-300">
                    <img
                      src={manager.image}
                      alt={manager.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="absolute -top-2 -right-2 w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full flex items-center justify-center">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                </div>

                <h3 className="text-3xl font-bold mb-2 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                  {manager.name}
                </h3>

                <p className="text-gray-500 font-semibold mb-3 text-xl">
                  {manager.role}
                </p>

                <p className="text-gray-600 mb-4">
                  {manager.speciality}
                </p>

                <p className="text-gray-600 text-lg">
                  {manager.description}
                </p>

                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-3xl" />
              </div>
            </div>
          </div>
        </section>

        {/* Section Placoplâtre */}
        {/* Section Placoplâtre Améliorée */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-50/50 to-cyan-50/50">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-cyan-100 px-6 py-3 rounded-full border border-blue-200/50 shadow-lg mb-6">
                <Layers className="w-5 h-5 text-blue-600 animate-pulse" />
                <span className="text-blue-700 font-semibold">Spécialité Placoplâtre</span>
              </div>

              <h2 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-6">
                Expertise en Placoplâtre
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Solutions modernes de cloisonnement et décoration intérieure avec des matériaux de haute qualité
              </p>

              {/* Définition du Placoplâtre */}
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-blue-100/50 max-w-4xl mx-auto mb-16">
                <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                  Qu'est-ce que le Placoplâtre ?
                </h3>
                <h4 className="text-xl font-semibold mb-4 text-gray-800" style={{ direction: 'rtl' }}>
                  ما هو الجبسوم بورد؟
                </h4>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  <div className="space-y-4 text-left">
                    <p className="text-gray-600 leading-relaxed">
                      Le placoplâtre, aussi appelé <strong className="text-blue-600">cloison sèche</strong> ou <strong className="text-blue-600">BA13</strong>,
                      est un matériau de construction constitué d'une âme en plâtre prise entre deux feuilles de carton.
                      Il révolutionne l'aménagement intérieur par sa facilité de pose et ses performances exceptionnelles.
                    </p>

                    <p className="text-gray-600 leading-relaxed" style={{ direction: 'rtl' }}>
                      الجبسوم بورد، المعروف أيضًا باسم <strong className="text-blue-600">الحائط الجاف</strong>،
                      هو مادة بناء تتكون من قلب من الجبس محاط بطبقتين من الكرتون.
                      يُحدث ثورة في التصميم الداخلي بسهولة التركيب والأداء الاستثنائي.
                    </p>
                  </div>

                  <div className="relative">
                    <div className="aspect-video bg-gradient-to-br from-blue-100 to-cyan-200 rounded-2xl shadow-xl overflow-hidden">
                      <img
                        src={`${CLOUDINARY_BASE}types-placoplatre/placo.jpg`}
                        alt="Installation placoplâtre"
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="absolute -bottom-4 -right-4 w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-2xl shadow-2xl flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Types de Placoplâtre avec Photos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              {[
                {
                  name: "Standard (BA13)",
                  nameAr: "عادي",
                  description: "Plaque de base pour cloisons et plafonds intérieurs",
                  descriptionAr: "لوح أساسي للحوائط والأسقف الداخلية",
                  image: `${CLOUDINARY_BASE}types-placoplatre/standard.jpg`,
                  color: "from-gray-500 to-slate-600",
                  uses: ["Cloisons intérieures", "Plafonds suspendus", "Doublages muraux"],
                  usesAr: ["الحوائط الداخلية", "الأسقف المعلقة", "تغليف الجدران"],
                  thickness: "13mm",
                  specs: "Résistance standard, économique"
                },
                {
                  name: "Hydrofuge (BA13H)",
                  nameAr: "مقاوم للرطوبة",
                  description: "Résistant à l'humidité pour zones humides",
                  descriptionAr: "مقاوم للرطوبة للمناطق الرطبة",
                  image: `${CLOUDINARY_BASE}types-placoplatre/hydrofuge.webp`,
                  color: "from-green-500 to-teal-600",
                  uses: ["Salles de bains", "Cuisines", "Buanderies"],
                  usesAr: ["الحمامات", "المطابخ", "غرف الغسيل"],
                  thickness: "13mm",
                  specs: "Traitement hydrofuge, cartonnage vert"
                },
                {
                  name: "Coupe feu",
                  nameAr: "مقاوم للحريق",
                  description: "Résistance au feu pour sécurité renforcée",
                  descriptionAr: "مقاومة للحريق لأمان معزز",
                  image: `${CLOUDINARY_BASE}types-placoplatre/coupe-feu.jpg`,
                  color: "from-red-500 to-orange-600",
                  uses: ["ERP", "Gaines techniques", "Couloirs de sécurité"],
                  usesAr: ["المباني العامة", "الأنابيب التقنية", "ممرات الأمان"],
                  thickness: "13mm",
                  specs: "Résistance feu 1h, cartonnage rose"
                },
                {
                  name: "Aléatoire",
                  nameAr: "عازل للصوت",
                  description: "Isolation phonique pour plus de confort",
                  descriptionAr: "عزل صوتي لمزيد من الراحة",
                  image: `${CLOUDINARY_BASE}types-placoplatre/aléatoire.jpg`,
                  color: "from-purple-500 to-indigo-600",
                  uses: ["Chambres", "Bureaux", "Studios"],
                  usesAr: ["غرف النوم", "المكاتب", "الاستوديوهات"],
                  thickness: "13mm",
                  specs: "Réduction bruit -42dB, âme alvéolaire"
                },
                {
                  name: "Extérieur",
                  nameAr: "خارجي",
                  description: "Solution de cloison ou bardage extérieur, haute résistance aux conditions extrêmes",
                  descriptionAr: "حل للجدران أو الكسوة الخارجية، مقاومة عالية للظروف القاسية",
                  image: `${CLOUDINARY_BASE}types-placoplatre/exterieur.png`,
                  color: "from-blue-700 to-gray-800",
                  uses: ["Façades", "Murs extérieurs", "Balcons", "Bardages"],
                  usesAr: ["الواجهات", "الجدران الخارجية", "الشرفات", "الكسوة"],
                  thickness: "12.5mm",
                  specs: "Plaque renforcée en fibre de verre, résistante à l’humidité, moisissures, chocs et conditions climatiques extrêmes. Faible absorption d’eau, facile à manipuler et à découper."
                }



              ].map((type, index) => (
                <div
                  key={index}
                  className="group relative bg-white/80 backdrop-blur-sm rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 border border-blue-100/50 transform hover:scale-[1.02]"
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={type.image}
                      alt={type.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                    {/* Badge Type */}
                    <div className={`absolute top-4 left-4 px-4 py-2 rounded-full bg-gradient-to-r ${type.color} text-white font-bold shadow-lg`}>
                      {type.thickness}
                    </div>

                    {/* Icône */}
                    <div className={`absolute top-4 right-4 w-12 h-12 rounded-2xl bg-gradient-to-r ${type.color} flex items-center justify-center shadow-lg group-hover:rotate-12 transition-transform duration-300`}>
                      <Layers className="w-6 h-6 text-white" />
                    </div>
                  </div>

                  {/* Contenu */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2 bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                      {type.name}
                    </h3>
                    <h4 className="text-lg font-semibold mb-3 text-gray-800" style={{ direction: 'rtl' }}>
                      {type.nameAr}
                    </h4>

                    <p className="text-gray-600 mb-2 text-sm">
                      {type.description}
                    </p>
                    <p className="text-gray-600 mb-4 text-sm" style={{ direction: 'rtl' }}>
                      {type.descriptionAr}
                    </p>

                    {/* Spécifications */}
                    <div className="bg-blue-50/50 rounded-xl p-3 mb-4">
                      <p className="text-sm font-semibold text-blue-700 mb-1">Spécifications:</p>
                      <p className="text-xs text-gray-600">{type.specs}</p>
                    </div>

                    {/* Utilisations */}
                    <div className="space-y-3">
                      <div>
                        <h4 className="font-semibold text-gray-700 text-sm mb-2">Applications:</h4>
                        <div className="grid grid-cols-1 gap-1">
                          {type.uses.map((use, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                              <CheckCircle className="w-3 h-3 text-blue-500 flex-shrink-0" />
                              <span>{use}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-700 text-sm mb-2" style={{ direction: 'rtl' }}>التطبيقات:</h4>
                        <div className="grid grid-cols-1 gap-1" style={{ direction: 'rtl' }}>
                          {type.usesAr.map((use, i) => (
                            <div key={`ar-${i}`} className="flex items-center gap-2 text-xs text-gray-600">
                              <CheckCircle className="w-3 h-3 text-blue-500 flex-shrink-0" />
                              <span>{use}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Effet de brillance */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                </div>
              ))}
            </div>

            {/* Avantages avec Photos */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
              <div className="space-y-6">
                <h3 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                  Pourquoi Choisir le Placoplâtre ?
                </h3>
                <h4 className="text-2xl font-semibold text-gray-800" style={{ direction: 'rtl' }}>
                  لماذا تختار الجبسوم بورد؟
                </h4>

                <div className="space-y-4">
                  {[
                    {
                      icon: "⚡",
                      title: "Installation Rapide",
                      titleAr: "تركيب سريع",
                      desc: "Pose 5x plus rapide qu'une cloison traditionnelle",
                      descAr: "تركيب أسرع 5 مرات من الحائط التقليدي"
                    },
                    {
                      icon: "🔧",
                      title: "Facilité de Modification",
                      titleAr: "سهولة التعديل",
                      desc: "Découpes et modifications aisées",
                      descAr: "قطع وتعديلات سهلة"
                    },
                    {
                      icon: "🎨",
                      title: "Finition Parfaite",
                      titleAr: "لمسة نهائية مثالية",
                      desc: "Surface lisse prête pour tous types de revêtements",
                      descAr: "سطح أملس جاهز لجميع أنواع الكسوة"
                    },
                    {
                      icon: "🏠",
                      title: "Isolation Performante",
                      titleAr: "عزل عالي الأداء",
                      desc: "Excellentes propriétés thermiques et acoustiques",
                      descAr: "خصائص حرارية وصوتية ممتازة"
                    }
                  ].map((advantage, i) => (
                    <div key={i} className="flex items-start gap-4 p-4 bg-white/60 rounded-2xl border border-blue-100/50">
                      <div className="text-2xl">{advantage.icon}</div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-800 mb-1">{advantage.title}</h4>
                        <h5 className="font-semibold text-gray-700 mb-2" style={{ direction: 'rtl' }}>{advantage.titleAr}</h5>
                        <p className="text-sm text-gray-600 mb-1">{advantage.desc}</p>
                        <p className="text-sm text-gray-600" style={{ direction: 'rtl' }}>{advantage.descAr}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Galerie Photos */}
              <div className="grid grid-cols-3 gap-4"> {/* Changé de 2 à 3 colonnes */}
                <div className="space-y-4">
                  <div className="h-32 bg-gradient-to-br from-blue-200 to-cyan-300 rounded-2xl shadow-xl overflow-hidden">
                    <img
                      src={`${CLOUDINARY_BASE}realisations/standard-real.jpg`}
                      alt="Placoplâtre standard"
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="h-48 bg-gradient-to-br from-cyan-200 to-blue-300 rounded-2xl shadow-xl overflow-hidden">
                    <img
                      src={`${CLOUDINARY_BASE}realisations/hydrofuge-real.jpg`}
                      alt="Placoplâtre hydrofuge"
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                </div>

                <div className="space-y-4 mt-8">
                  <div className="h-48 bg-gradient-to-br from-purple-200 to-pink-300 rounded-2xl shadow-xl overflow-hidden">
                    <img
                      src={`${CLOUDINARY_BASE}realisations/coupe-feu-real.jpg`}
                      alt="Placoplâtre coupe-feu"
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="h-32 bg-gradient-to-br from-orange-200 to-red-300 rounded-2xl shadow-xl overflow-hidden">
                    <img
                      src={`${CLOUDINARY_BASE}realisations/exterieur-real.jpg`}
                      alt="Placoplâtre extérieur"
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                </div>

                {/* Nouvelle colonne avec photo aléatoire */}
                <div className="space-y-4">
                  <div className="h-64 bg-gradient-to-br from-green-200 to-emerald-300 rounded-2xl shadow-xl overflow-hidden">
                    <img
                      src={`${CLOUDINARY_BASE}realisations/aléatoire-real.jpg`}
                      alt="Placoplâtre aléatoire"
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Processus d'Installation */}
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-blue-100/50">
              <h3 className="text-2xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent text-center">
                Notre Processus d'Installation
              </h3>
              <h4 className="text-xl font-semibold mb-8 text-gray-800 text-center" style={{ direction: 'rtl' }}>
                عملية التركيب لدينا
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[
                  {
                    step: "01",
                    title: "Étude & Mesures",
                    titleAr: "دراسة وقياسات",
                    desc: "Analyse précise de votre espace",
                    descAr: "تحليل دقيق لمساحتك",
                    icon: "📏"
                  },
                  {
                    step: "02",
                    title: "Préparation",
                    titleAr: "التحضير",
                    desc: "Préparation du support et matériaux",
                    descAr: "تحضير الدعم والمواد",
                    icon: "🔨"
                  },
                  {
                    step: "03",
                    title: "Installation",
                    titleAr: "التركيب",
                    desc: "Pose professionnelle des plaques",
                    descAr: "تركيب احترافي للألواح",
                    icon: "⚒️"
                  },
                  {
                    step: "04",
                    title: "Finitions",
                    titleAr: "اللمسة الأخيرة",
                    desc: "Joints, ponçage et finition parfaite",
                    descAr: "الوصلات والصنفرة واللمسة المثالية",
                    icon: "✨"
                  }
                ].map((process, i) => (
                  <div key={i} className="text-center group">
                    <div className="relative mb-4">
                      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-2xl group-hover:rotate-12 transition-transform duration-300">
                        {process.step}
                      </div>
                      <div className="absolute -top-2 -right-2 text-2xl">
                        {process.icon}
                      </div>
                    </div>

                    <h4 className="font-bold text-gray-800 mb-1">{process.title}</h4>
                    <h5 className="font-semibold text-gray-700 mb-2 text-sm" style={{ direction: 'rtl' }}>{process.titleAr}</h5>
                    <p className="text-sm text-gray-600 mb-1">{process.desc}</p>
                    <p className="text-sm text-gray-600" style={{ direction: 'rtl' }}>{process.descAr}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>



        {/* CTA Final */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto text-center">
            <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-3xl p-12 shadow-2xl relative overflow-hidden">
              {/* Effet de brillance */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full animate-pulse" />

              <div className="relative z-10 space-y-8">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center">
                    <Zap className="w-10 h-10 text-white animate-pulse" />
                  </div>
                </div>

                <h2 className="text-4xl sm:text-5xl font-black text-white mb-6">
                  Prêt à Transformer Votre Espace ?
                </h2>

                <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
                  Contactez-nous dès aujourd'hui pour une consultation gratuite et découvrez comment nous pouvons
                  donner vie à vos idées les plus audacieuses.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button className="group relative flex items-center gap-3 bg-white text-amber-600 px-8 py-4 rounded-2xl font-bold shadow-2xl hover:shadow-white/25 transition-all duration-500 overflow-hidden transform hover:scale-105">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                    <Play className="w-5 h-5 relative z-10 group-hover:animate-bounce" />
                    <span className="relative z-10">Démarrer un Projet</span>
                  </button>

                  <a
                    href="tel:+21697286007"
                    className="group flex items-center gap-3 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-2xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-500 border border-white/20 hover:border-white/40"
                  >
                    <Phone className="w-5 h-5 group-hover:animate-bounce" />
                    <span>+216 97 286 007</span>
                    <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>


      </div>
    </>
  );
};

export default AboutPage;